import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { GeoLocationMultiSelect } from '../../shared/components/GeoLocationMultiSelect';
import { StatusChip } from '../../shared/components/StatusChip';
import { resources } from '../../core/services/resources';
import type {
  AIModel,
  LineOfBizCategory,
  LineOfBizPersona,
  PromptFunnelStage,
  PromptImportance,
} from '../../core/types/data-models';

const IMPORTANCES: PromptImportance[] = ['LOW', 'MEDIUM', 'HIGH', 'VERY_HIGH'];

/**
 * Per-field choice for the four prompt-derived attributes. `useDefault`
 * means "keep each prompt's own value" (the prompt's `default*` field);
 * otherwise `value` is applied to every prompt in the batch.
 */
interface DefaultableNumber {
  useDefault: boolean;
  value: number | null;
}
interface DefaultableImportance {
  useDefault: boolean;
  value: PromptImportance;
}

/**
 * Parameters chosen in the bulk-attach dialog. Geo + AI models are set
 * explicitly for the whole batch; category / persona / stage / importance
 * each either fall back to every prompt's own default or override it.
 */
export interface BulkAttachValues {
  geoLocationIds: number[];
  aiModelIds: number[];
  category: DefaultableNumber;
  persona: DefaultableNumber;
  stage: DefaultableNumber;
  importance: DefaultableImportance;
}

const INITIAL_VALUES: BulkAttachValues = {
  geoLocationIds: [],
  aiModelIds: [],
  category: { useDefault: true, value: null },
  persona: { useDefault: true, value: null },
  stage: { useDefault: true, value: null },
  importance: { useDefault: true, value: 'MEDIUM' },
};

interface ReportTemplateBulkAttachDialogProps {
  open: boolean;
  /** How many prompts will be attached — shown in the header. */
  promptCount: number;
  onClose: () => void;
  onSave: (values: BulkAttachValues) => Promise<void>;
  saving?: boolean;
  error?: string | null;
  onErrorClose?: () => void;
  /** AIModel ids the owning LOB supports; undefined = full catalog. */
  supportedAiModelIds?: number[];
  /** GeoLocation ids the owning LOB supports; undefined = full catalog. */
  supportedGeoLocationIds?: number[];
}

/**
 * Batch "Add prompts to Report Template" dialog. Lets the user define the
 * `ReportTemplatePrompt` parameters once and apply them to every selected
 * prompt at attach time. Category, persona, stage and importance default
 * to each prompt's own value (the "Default" toggle) unless the user picks
 * an explicit override; geo locations and AI models are set for the whole
 * batch.
 */
export function ReportTemplateBulkAttachDialog({
  open,
  promptCount,
  onClose,
  onSave,
  saving,
  error,
  onErrorClose,
  supportedAiModelIds,
  supportedGeoLocationIds,
}: ReportTemplateBulkAttachDialogProps) {
  const [values, setValues] = useState<BulkAttachValues>(INITIAL_VALUES);

  // Reset whenever the dialog opens so picks don't leak across batches.
  useEffect(() => {
    if (open) setValues(INITIAL_VALUES);
  }, [open]);

  const aiModels = useQuery({
    queryKey: ['ai-models', 'select'],
    queryFn: () => resources.aiModels.list({ pageSize: 200, sort: 'aiEngine' }),
  });

  // Catalog filtered to the LOB's supported models (by numeric AIModel.id).
  // undefined = unrestricted (LOB not loaded yet) → show the full list.
  const allowedAiModels = useMemo(
    () =>
      supportedAiModelIds === undefined
        ? aiModels.data?.data ?? []
        : (aiModels.data?.data ?? []).filter((m) => supportedAiModelIds.includes(m.id)),
    [aiModels.data, supportedAiModelIds],
  );

  // Resolve each selected id to its AIModel row for chip labels.
  const aiModelOptions = useMemo(
    () =>
      values.aiModelIds
        .map((id) => (aiModels.data?.data ?? []).find((m) => m.id === id))
        .filter((m): m is AIModel => !!m),
    [aiModels.data, values.aiModelIds],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(values);
  };

  return (
    <FormDialog
      open={open}
      title="Add prompts to template"
      submitLabel={saving ? 'Adding…' : `Add ${promptCount} to template`}
      loading={saving}
      onClose={onClose}
      onSubmit={handleSubmit}
      maxWidth="md"
    >
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="body2">
          <strong>{promptCount}</strong> {promptCount === 1 ? 'prompt' : 'prompts'} will be
          attached. Category, persona, stage and importance keep each prompt's own value
          unless you override them below; geo locations and AI models apply to all.
        </Typography>
      </Paper>

      {error && (
        <Alert severity="error" onClose={onErrorClose}>{error}</Alert>
      )}

      <Stack direction="row" spacing={2}>
        <DefaultableField
          label="category"
          useDefault={values.category.useDefault}
          onUseDefaultChange={(useDefault) =>
            setValues((v) => ({ ...v, category: { ...v.category, useDefault } }))
          }
        >
          <EntitySelect<LineOfBizCategory>
            cacheKey={['line-of-biz-categories', 'select']}
            service={resources.lineOfBizCategories}
            label="Category"
            value={values.category.value}
            onChange={(id) =>
              setValues((v) => ({ ...v, category: { ...v.category, value: id } }))
            }
          />
        </DefaultableField>
        <DefaultableField
          label="persona"
          useDefault={values.persona.useDefault}
          onUseDefaultChange={(useDefault) =>
            setValues((v) => ({ ...v, persona: { ...v.persona, useDefault } }))
          }
        >
          <EntitySelect<LineOfBizPersona>
            cacheKey={['line-of-biz-personas', 'select']}
            service={resources.lineOfBizPersonas}
            label="Persona"
            value={values.persona.value}
            onChange={(id) =>
              setValues((v) => ({ ...v, persona: { ...v.persona, value: id } }))
            }
          />
        </DefaultableField>
      </Stack>

      <Stack direction="row" spacing={2}>
        <DefaultableField
          label="stage"
          useDefault={values.stage.useDefault}
          onUseDefaultChange={(useDefault) =>
            setValues((v) => ({ ...v, stage: { ...v.stage, useDefault } }))
          }
        >
          <EntitySelect<PromptFunnelStage>
            cacheKey={['prompt-funnel-stages', 'select']}
            service={resources.promptFunnelStages}
            label="Stage"
            value={values.stage.value}
            onChange={(id) =>
              setValues((v) => ({ ...v, stage: { ...v.stage, value: id } }))
            }
          />
        </DefaultableField>
        <DefaultableField
          label="importance"
          useDefault={values.importance.useDefault}
          onUseDefaultChange={(useDefault) =>
            setValues((v) => ({ ...v, importance: { ...v.importance, useDefault } }))
          }
        >
          <FormControl fullWidth>
            <InputLabel id="rt-bulk-importance-label">Importance</InputLabel>
            <Select
              labelId="rt-bulk-importance-label"
              label="Importance"
              value={values.importance.value}
              renderValue={(v) => <StatusChip value={v as string} />}
              onChange={(e) =>
                setValues((v) => ({
                  ...v,
                  importance: { ...v.importance, value: e.target.value as PromptImportance },
                }))
              }
            >
              {IMPORTANCES.map((i) => (
                <MenuItem key={i} value={i}>
                  <StatusChip value={i} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DefaultableField>
      </Stack>

      <Stack direction="row" spacing={2}>
        <Autocomplete<AIModel, true, false, false>
          multiple
          disableCloseOnSelect
          fullWidth
          options={[
            ...allowedAiModels,
            ...aiModelOptions.filter((m) => !allowedAiModels.some((a) => a.id === m.id)),
          ]}
          value={aiModelOptions}
          onChange={(_, next) =>
            setValues((v) => ({ ...v, aiModelIds: next.map((m) => m.id) }))
          }
          getOptionLabel={(m) => `${m.name} (${m.aiModelIdentifier})`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          loading={aiModels.isLoading}
          renderOption={(props, option, { selected }) => (
            <Box component="li" {...props} key={option.id}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.aiEngine} · {option.aiModelIdentifier}
                </Typography>
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="AI models"
              helperText="Pick one or more — each prompt is run against every selected model."
            />
          )}
        />
        <GeoLocationMultiSelect
          value={values.geoLocationIds}
          onChange={(ids) => setValues((v) => ({ ...v, geoLocationIds: ids }))}
          allowedIds={supportedGeoLocationIds}
          helperText="Zero or more countries these assignments target."
        />
      </Stack>
    </FormDialog>
  );
}

/**
 * Wraps an override picker with a "Default" toggle. When checked the
 * picker is hidden and the batch keeps each prompt's own value; when
 * unchecked the picker is shown and its value overrides the whole batch.
 */
function DefaultableField({
  label,
  useDefault,
  onUseDefaultChange,
  children,
}: {
  label: string;
  useDefault: boolean;
  onUseDefaultChange: (useDefault: boolean) => void;
  children: ReactNode;
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={useDefault}
            onChange={(e) => onUseDefaultChange(e.target.checked)}
          />
        }
        label={
          <Typography variant="body2">
            Default — keep each prompt&apos;s {label}
          </Typography>
        }
      />
      {!useDefault && <Box sx={{ mt: 0.5 }}>{children}</Box>}
    </Box>
  );
}
